/**
 * OsLogo
 *
 * Renderiza el ícono de marca del SO del agente + caption con la versión.
 * Mapea `os` + `osVersion` (strings reportados por el agente en agent.hello)
 * a un SVG en /public/os-logos/<key>.svg y a una etiqueta legible.
 *
 * Status online/offline se codifica con opacidad: 1.0 si está conectado,
 * 0.4 si está offline. El CircleIcon al lado del hostname sigue siendo el
 * indicador fuerte de estado (no compite con el color de marca del logo).
 *
 * Pure helpers (resolveOsFamily, resolveCaption, etc.) live in
 * `osLogoHelpers.ts` so this module only exports React components — keeps
 * fast-refresh working in dev.
 */
import { Box, Typography } from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import { resolveOsFamily, resolveLinuxDistro, resolveCaption, logoFileKey } from './osLogoHelpers';

export interface OsLogoProps {
  /** runtime.GOOS-style identifier: "darwin" | "windows" | "linux" (case-insensitive). */
  os?: string;
  /** Human-readable OS version reported by the agent. */
  osVersion?: string;
  /** Connection status — used to fade the logo when the agent is offline. */
  connected: boolean;
  /** Logo edge size in px. Default 40. Caption is sized independently. */
  size?: number;
}

export const OsLogo: React.FC<OsLogoProps> = ({ os, osVersion, connected, size = 40 }) => {
  const family = resolveOsFamily(os);
  const caption = family ? resolveCaption(family, osVersion) : '—';
  const linuxKey = family === 'linux' ? resolveLinuxDistro(osVersion).key : null;
  const imgSrc =
    family === 'linux'
      ? `/os-logos/${linuxKey}.svg`
      : family
        ? `/os-logos/${logoFileKey(family)}.svg`
        : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 0.5,
        minWidth: size + 16,
      }}
    >
      {family ? (
        <Box
          component="img"
          src={imgSrc}
          alt={`${family} logo`}
          loading="lazy"
          sx={{
            width: size,
            height: size,
            opacity: connected ? 1 : 0.4,
            transition: 'opacity 0.2s',
            display: 'block',
            objectFit: 'contain',
          }}
          // Si el distro-specific falla, fallback al logo genérico de Linux.
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            if (family === 'linux' && linuxKey !== 'linux' && linuxKey) {
              e.currentTarget.src = '/os-logos/linux.svg';
            }
          }}
        />
      ) : (
        // Sin OS reportado: ícono genérico en lugar de un <img> con src roto.
        <ComputerIcon
          aria-label="sistema operativo desconocido"
          sx={{
            fontSize: size,
            opacity: connected ? 1 : 0.4,
            color: 'text.disabled',
            transition: 'opacity 0.2s',
          }}
        />
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontSize: '0.7rem',
          lineHeight: 1.2,
          textAlign: 'center',
          maxWidth: size + 40,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={caption}
      >
        {caption}
      </Typography>
    </Box>
  );
};
